import React, { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  CalendarFetchByWeek,
  getMondayGivenWeekDate,
  getSundayGivenWeekDate,
} from 'components/OrderListTable/CalendarFetch/CalendarFetchByWeek';
import PageVisibility from 'react-page-visibility';
import { useFetchShiftByRegion } from 'hooks/useFetchShift';
import { WeekDayForAdmin } from './WeekDayForAdmin/WeekDayForAdmin';
import { isSameDayAsFilterDay } from 'utils/date';
import { asyncAddShiftCourierToNextDay } from './updateShift';
import { Helmet } from 'react-helmet';
import './Shift.scss';
import { ReqEnum } from '../../const/requestStatus';
import {
  ShiftCourierJson,
  ShiftCourierWithDateJson,
  UpdateType,
  ShiftStatusType,
} from '../../const/ShiftConst';
import Swal from 'sweetalert2';
import { Region, reqStatus } from '../../types/orderTypes';

export function getWeekArray(startDate: Dayjs, endDate: Dayjs): Dayjs[] {
  return [
    startDate,
    startDate.add(1, 'day'),
    startDate.add(2, 'day'),
    startDate.add(3, 'day'),
    startDate.add(4, 'day'),
    startDate.add(5, 'day'),
    endDate,
  ];
}

function onVisibilityChange(
  isVisible: boolean,
  setUpdatedTime: (time: Dayjs) => void
): void {
  if (isVisible) {
    setUpdatedTime(dayjs());
  }
}

export function OneWeekForAdmin({ region }: { region: Region }): JSX.Element {
  const [updatedTime, setUpdatedTime] = useState<Dayjs>(dayjs());
  const [startDate, setStartDate] = useState<Dayjs>(getMondayGivenWeekDate());
  const [endDate, setEndDate] = useState<Dayjs>(getSundayGivenWeekDate());
  const [shiftCourierWithDateList, fetchStatus] = useFetchShiftByRegion(
    startDate,
    endDate,
    region,
    updatedTime.format('HH:mm')
  );
  const [weekOfDates, setWeekOfDates] = useState<Dayjs[]>(
    getWeekArray(startDate, endDate)
  );

  const fetchValues = (values: {
    fetchStartDate: Dayjs;
    fetchEndDate: Dayjs;
  }): void => {
    const { fetchStartDate, fetchEndDate } = values;
    setStartDate(fetchStartDate);
    setEndDate(fetchEndDate);
    setWeekOfDates(getWeekArray(fetchStartDate, fetchEndDate));
  };

  return (
    <PageVisibility
      onChange={(isVisible: boolean) =>
        onVisibilityChange(isVisible, setUpdatedTime)
      }
    >
      <OneWeekForAdminUI
        shiftCourierWithDateList={shiftCourierWithDateList}
        weekOfDates={weekOfDates}
        fetchStatus={fetchStatus}
        fetchValues={fetchValues}
        startDate={startDate}
        endDate={endDate}
        region={region}
      />
    </PageVisibility>
  );
}

export function OneWeekForAdminUI({
  shiftCourierWithDateList,
  weekOfDates,
  fetchStatus,
  fetchValues,
  startDate,
  endDate,
  region,
}: {
  shiftCourierWithDateList: ShiftCourierWithDateJson[];
  weekOfDates: Dayjs[];
  fetchStatus: reqStatus;
  fetchValues: (vals: { fetchStartDate: Dayjs; fetchEndDate: Dayjs }) => void;
  startDate: Dayjs;
  endDate: Dayjs;
  region: Region;
}): JSX.Element {
  return (
    <div className="shiftCalendar">
      <div className="calenderBox">
        <div className="calendarTable">
          <div className="positionWeek">
            <CalendarFetchByWeek
              fetchValues={fetchValues}
              givenStartDate={startDate}
              givenEndDate={endDate}
            />
          </div>

          {fetchStatus === ReqEnum.SUCCESS && (
            // NOTE: OneWeekOfDaysForAdmin captures courierListBookedWeek in a state.
            // So each time this is updated, we need to fore a recreation (not re-render)
            // of the component to make it recognice the new state. Right now this works
            // because fetchStatus changes to PENDING and then back to SUCCESS when
            // reloading state
            <OneWeekOfDaysForAdmin
              courierListBookedWeek={shiftCourierWithDateList}
              weekOfDates={weekOfDates}
              region={region}
            />
          )}
        </div>
      </div>
    </div>
  );
}

async function addShiftCourierNextDay(
  shiftCourierToAddNextDay: ShiftCourierJson,
  courierListBookedWeekState: ShiftCourierWithDateJson[],
  index: number,
  region: Region
): Promise<ShiftCourierWithDateJson[]> {
  const newWeekState: Promise<ShiftCourierWithDateJson[]> = Promise.all(
    courierListBookedWeekState.map(
      async (obj: ShiftCourierWithDateJson, weekIndex: number) => {
        if (index === weekIndex - 1) {
          const newShiftCourierForNextDay: ShiftCourierJson =
            await asyncAddShiftCourierToNextDay(
              shiftCourierToAddNextDay,
              dayjs(obj.date),
              region
            );

          const DayCourierListBooked: ShiftCourierJson[] =
            courierListBookedWeekState[index + 1].courierDayBooked;

          const newWeekDay: ShiftCourierWithDateJson = {
            date: obj.date,
            courierDayBooked: [
              ...DayCourierListBooked,
              newShiftCourierForNextDay,
            ],
          };
          return newWeekDay;
        } else {
          return obj;
        }
      }
    )
  );

  // how to do it for next week
  return newWeekState;
}

const DayOfWeek = ({
  courierListBookedWeekState,
  index,
  date,
  region,
  setCourierListBookedWeekState,
  updateShiftCourierListForDay,
}: {
  courierListBookedWeekState: any;
  index: number;
  date: Dayjs;
  region: Region;
  setCourierListBookedWeekState: (newState: ShiftCourierWithDateJson[]) => void;
  updateShiftCourierListForDay: (
    shiftCourierList: ShiftCourierJson[],
    date: dayjs.Dayjs,
    updateType: UpdateType
  ) => void;
}): JSX.Element => {
  const shiftDate = courierListBookedWeekState[index]?.date;

  if (isSameDayAsFilterDay(shiftDate, date)) {
    const addShiftCourierNextDayMethod = async (
      shiftCourierToAddNextDay: ShiftCourierJson
    ) => {
      const listWithoutDeletedAndWishForNextDay: number[] =
        courierListBookedWeekState[index + 1].courierDayBooked
          .filter(obj => obj.status !== ShiftStatusType.DELETED)
          .filter(obj => obj.status !== ShiftStatusType.WISH)
          .map(obj => obj.courierId);

      const isCourierIdNotInNextDay =
        listWithoutDeletedAndWishForNextDay.indexOf(
          Number(shiftCourierToAddNextDay.courierId)
        ) === -1;

      if (isCourierIdNotInNextDay) {
        const newWeekState: ShiftCourierWithDateJson[] =
          await addShiftCourierNextDay(
            shiftCourierToAddNextDay,
            courierListBookedWeekState,
            index,
            region
          );

        setCourierListBookedWeekState(newWeekState);
      } else {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: 'The courier is already available on the next day',
          showConfirmButton: false,
          timer: 1500,
        });
      }
    };

    return (
      <WeekDayForAdmin
        courierListBookedObj={courierListBookedWeekState[index]}
        addShiftCourierNextDayMethod={addShiftCourierNextDayMethod}
        updateShiftCourierListForDay={updateShiftCourierListForDay}
        region={region}
      />
    );
  } else {
    return <div />;
  }
};

// A whole week, the display for admin (unlike for couriers, which is in ShiftForCourier.tsx)
function OneWeekOfDaysForAdmin({
  courierListBookedWeek,
  weekOfDates,
  region,
}: {
  courierListBookedWeek: ShiftCourierWithDateJson[];
  weekOfDates: Dayjs[];
  region: Region;
}) {
  const sortedShiftCourierWithDateList: ShiftCourierWithDateJson[] =
    courierListBookedWeek.map((obj: ShiftCourierWithDateJson) => {
      const sortedCourierDayBooked: ShiftCourierJson[] =
        obj.courierDayBooked.sort((a: ShiftCourierJson, b: ShiftCourierJson) =>
          a.name.localeCompare(b.name)
        );

      const sortedShiftCourierWithDate: ShiftCourierWithDateJson = {
        date: obj.date,
        courierDayBooked: sortedCourierDayBooked,
      };
      return sortedShiftCourierWithDate;
    });

  const [courierListBookedWeekState, setCourierListBookedWeekState] = useState<
    ShiftCourierWithDateJson[]
  >(sortedShiftCourierWithDateList);

  const updateShiftCourierListForDay = (
    shiftCourierList: ShiftCourierJson[],
    date: Dayjs,
    updateType: UpdateType
  ) => {
    setCourierListBookedWeekState(currentState =>
      currentState.map((obj: ShiftCourierWithDateJson) => {
        if (
          dayjs(obj.date).day() === dayjs(date).day() &&
          updateType === UpdateType.ADD
        ) {
          const newWeekDay: ShiftCourierWithDateJson = {
            date: obj.date,
            courierDayBooked: [...obj.courierDayBooked, ...shiftCourierList],
          };
          return newWeekDay;
        } else if (
          dayjs(obj.date).day() === dayjs(date).day() &&
          updateType === UpdateType.REPLACE
        ) {
          const newWeekDay: ShiftCourierWithDateJson = {
            date: obj.date,
            courierDayBooked: shiftCourierList,
          };
          return newWeekDay;
        } else {
          return obj;
        }
      })
    );
  };

  return (
    <div className="oneWeek">
      {/* <Day date={dayjs()} /> */}
      {weekOfDates.map((date: Dayjs, index: number) => (
        <DayOfWeek
          key={dayjs(date).format('DD/MM')}
          courierListBookedWeekState={courierListBookedWeekState}
          index={index}
          date={date}
          region={region}
          setCourierListBookedWeekState={setCourierListBookedWeekState}
          updateShiftCourierListForDay={updateShiftCourierListForDay}
        />
      ))}
    </div>
  );
}

export function Shift({ region }: { region: Region }): JSX.Element {
  return (
    <>
      <Helmet>
        <title>Shifts</title>
      </Helmet>
      <OneWeekForAdmin region={region} />
    </>
  );
}
